import { InputKey } from './inputKeys';

interface IActionConfig {
	/** Клавиша по умолчанию */
	defaultKey: InputKey;

	/** Может ли игрок изменять клавишу в настройках */
	isEditable: boolean;
	/** Описание для настроек (обязательно, если isEditable true) */
	description?: string;
}

interface IMultiActionConfig {
	/** Комбинация клавиш по умолчанию */
	defaultKeys: InputKey[];

	/** Может ли игрок изменять комбинацию клавиш в настройках */
	isEditable: boolean;
	/** Описание для настроек (обязательно, если isEditable true) */
	description?: string;
}

export enum MouseButton {
	Left,
	Middle,
	Right,
}

export enum KeyPressStates {
	Pressed = 'Pressed',
	Unpressed = 'Unpressed',
	Both = 'Both',
}

export enum InputAction {
	/** Common */
	CommonTabNavLeft = 'common:tab_nav_left',
	CommonTabNavRight = 'common:tab_nav_right',

	CommonMenuNavLeft = 'common:menu_nav_left',
	CommonMenuNavRight = 'common:menu_nav_right',
	CommonMenuNavUp = 'common:menu_nav_up',
	CommonMenuNavDown = 'common:menu_nav_down',

	CommonSubMenuNavLeft = 'common:sub_menu_nav_left',
	CommonSubMenuNavRight = 'common:sub_menu_nav_right',

	CommonMenuAccept = 'common:menu_accept',
	CommonMenuClose = 'common:menu_close',

	CommonMenuRemoveItem = 'common:menu_remove_item',
	CommonMenuToggleInfo = 'common:menu_toggle_info',
	CommonMenuCameraLook = 'common:menu_camera_look',

	CommonInteract = 'common:interact',

	/** PlayerVehicles */
	PlayerVehiclesLockVehicle = 'playerVehicles:lock_vehicle',
	PlayerVehiclesToggleMenu = 'playerVehicles:toggle_menu',

	/** Vehicle */
	VehicleToggleSeatbelt = 'vehicle:toggle_seatbelt',
	VehicleToggleEngine = 'vehicle:toggle_engine',
	VehicleToggleTurnLightLeft = 'vehicle:toggle_turn_light_left',
	VehicleToggleTurnLightRight = 'vehicle:toggle_turn_light_right',
	VehicleToggleHeadlights = 'vehicle:toggle_headlights',
	VehicleToggleLock = 'vehicle:toggle_lock',
	VehicleRepair = 'vehicle:repair',
	VehicleHandlingModeNext = 'vehicle:handling_mode_next',
	VehicleHandlingModePrev = 'vehicle:handling_mode_prev',

	/** HUD */
	HudChatShow = 'hud:chat_show',
	HudKeysHelpShow = 'hud:keys_help_show',

	/** Phone */
	PhoneToggle = 'phone:toggle',

	/** PhotoMode */
	PhotoModeMoveForwards = 'photoMode:move_forwards',
	PhotoModeMoveLeft = 'photoMode:move_left',
	PhotoModeMoveBackwards = 'photoMode:move_backwards',
	PhotoModeMoveRight = 'photoMode:move_right',
	PhotoModeRollLeft = 'photoMode:roll_left',
	PhotoModeRollRight = 'photoMode:roll_right',
	PhotoModeMoveUp = 'photoMode:move_up',
	PhotoModeMoveDown = 'photoMode:move_down',
	PhotoModeMoveSlow = 'photoMode:move_slow',
	PhotoModeMoveFast = 'photoMode:move_fast',
	PhotoModeToggleDev = 'photoMode:toggle_dev',
	PhotoModeToggleInterfaceHidden = 'photoMode:toggle_interface_hidden',
	PhotoModeToggleMode = 'photoMode:toggle_mode',
	PhotoModeToggleFilter = 'photoMode:toggle_filter',
	PhotoModeToggleLogoPosition = 'photoMode:toggle_logo_position',
	PhotoModeToggleFormat = 'photoMode:toggle_format',
	PhotoModeToggleGrid = 'photoMode:toggle_grid',
	PhotoModeResetSettings = 'photoMode:reset_settings',

	/** Airbreak */
	AirbreakToggle = 'airbreak:toggle',

	/** Camhack */
	CamhackToggleOrTeleport = 'camhack:toggle_or_teleport',
	CamhackToggleFromSpectate = 'camhack:toggle_from_spectate',

	/** HomeBuying */
	HomeBuyingCloseHome = 'homeBuying:close_home',
	HomeBuyingSellHome = 'homeBuying:sell_home',
	HomeBuyingBack = 'homeBuying:back',

	/** Handling */
	HandlingSave = 'handling:save',
	HandlingSetFavorite = 'handling:set_favorite',
	HandlingPanelInteract = 'handling:panel_interact',
	HandlingResetAll = 'handling:reset_all',

	/** ClothesShop */
	ClothesShopToggleCart = 'clothesShop:toggle_cart',

	/** CarPaint */
	CarPaintShowCart = 'carPaint:show_cart',

	/** Tuning */
	TuningShowCart = 'tuning:show_cart',

	/** GameplayRecording */
	GameplayRecordingStart = 'gameplayRecording:start',
	GameplayRecordingStop = 'gameplayRecording:stop',

	/** Gameplay */
	GameplayTogglePauseMenu = 'gameplay:toggle_pause_menu',

	/** CharacterCreation */
	CharacterCreationChangeGender = 'characterCreation:change_gender',
	CharacterCreationRandom = 'characterCreation:random',

	/** MainMenu */
	MainMenuToggle = 'mainMenu:toggle',

	/** RadialMenu */
	RadialMenuToggle = 'radialMenu:toggle',

	/** Voice */
	VoiceActivate = 'voice:activate',

	/** LootBuyers */
	LootBuyersSellAll = 'lootBuyers:sell_all',

	/** HobbyFishing */
	HobbyFishingCatchFishSpace = 'hobbyFishing:catch_fish_space',
	HobbyFishingCatchFishA = 'hobbyFishing:catch_fish_a',
	HobbyFishingCatchFishD = 'hobbyFishing:catch_fish_d',
	HobbyFishingStart = 'hobbyFishing:start',

	/** GasStation */
	GasStationDrainFuel = 'gasStation:drain_fuel',
	GasStationFullTank = 'gasStation:full_tank',
	GasStationNavLeft = 'gasStation:nav_left',
	GasStationNavRight = 'gasStation:nav_right',

	/** Inventory */
	InventoryFastMove = 'inventory:fast_move',
	InventoryPageToggle = 'inventory:page_toggle',

	InventoryUseHotbarSlot1 = 'inventory:use_hotbar_slot_1',
	InventoryUseHotbarSlot2 = 'inventory:use_hotbar_slot_2',
	InventoryUseHotbarSlot3 = 'inventory:use_hotbar_slot_3',
	InventoryUseHotbarSlot4 = 'inventory:use_hotbar_slot_4',

	/** CatchRing */
	CatchRingCatch = 'catchRing:catch',

	/** RockPaperScissors */
	RockPaperScissorsSelectRock = 'rockPaperScissors:select_rock',
	RockPaperScissorsSelectPaper = 'rockPaperScissors:select_paper',
	RockPaperScissorsSelectScissors = 'rockPaperScissors:select_scissors',

	/** Invite */
	InviteAccept = 'invite:accept',
	InviteDecline = 'invite:decline',

	/** FeedbackLight */
	FeedbackOpen = 'feedback:open',

	/** AdminPanel */
	AdminPanelToggle = 'adminPanel:toggle',
}

const inputActionsConfig: { [key in keyof typeof InputAction]: IActionConfig } = {
	/** Common */
	CommonTabNavLeft: {
		defaultKey: InputKey.Q,
		isEditable: false,
	},
	CommonTabNavRight: {
		defaultKey: InputKey.E,
		isEditable: false,
	},

	CommonMenuNavLeft: {
		defaultKey: InputKey.ArrowLeft,
		isEditable: false,
	},
	CommonMenuNavRight: {
		defaultKey: InputKey.ArrowRight,
		isEditable: false,
	},
	CommonMenuNavUp: {
		defaultKey: InputKey.ArrowUp,
		isEditable: false,
	},
	CommonMenuNavDown: {
		defaultKey: InputKey.ArrowDown,
		isEditable: false,
	},

	CommonSubMenuNavLeft: {
		defaultKey: InputKey.A,
		isEditable: false,
	},
	CommonSubMenuNavRight: {
		defaultKey: InputKey.D,
		isEditable: false,
	},

	CommonMenuAccept: {
		defaultKey: InputKey.Enter,
		isEditable: false,
	},
	CommonMenuClose: {
		defaultKey: InputKey.Escape,
		isEditable: false,
	},

	CommonMenuRemoveItem: {
		defaultKey: InputKey.Delete,
		isEditable: false,
	},
	CommonMenuToggleInfo: {
		defaultKey: InputKey.I,
		isEditable: false,
	},
	CommonMenuCameraLook: {
		defaultKey: InputKey.MouseRight,
		isEditable: false,
	},

	CommonInteract: {
		defaultKey: InputKey.E,
		isEditable: false,
	},

	/** PlayerVehicles */
	PlayerVehiclesLockVehicle: {
		defaultKey: InputKey.L,
		isEditable: false,
	},
	PlayerVehiclesToggleMenu: {
		defaultKey: InputKey.F3,
		isEditable: true,
		description: 'Открыть меню транспорта',
	},

	/** Vehicle */
	VehicleToggleSeatbelt: {
		defaultKey: InputKey.Three,
		isEditable: true,
		description: 'Пристегнуть ремень безопасности',
	},
	VehicleToggleEngine: {
		defaultKey: InputKey.Two,
		isEditable: true,
		description: 'Завести/заглушить двигатель',
	},
	VehicleToggleTurnLightLeft: {
		defaultKey: InputKey.Five,
		isEditable: true,
		description: 'Включить/выключить левый поворотник',
	},
	VehicleToggleTurnLightRight: {
		defaultKey: InputKey.Six,
		isEditable: true,
		description: 'Включить/выключить правый поворотник',
	},
	VehicleToggleHeadlights: {
		defaultKey: InputKey.H,
		isEditable: true,
		description: 'Переключить режим фар (ближний/дальний/выкл)',
	},
	VehicleToggleLock: {
		defaultKey: InputKey.Four,
		isEditable: true,
		description: 'Запереть/отпереть двери',
	},
	VehicleRepair: {
		defaultKey: InputKey.One,
		isEditable: true,
		description: 'Починить транспорт',
	},
	VehicleHandlingModePrev: {
		defaultKey: InputKey.ArrowLeft,
		isEditable: true,
		description: 'Предыдущий режим хендлинга транспорта',
	},
	VehicleHandlingModeNext: {
		defaultKey: InputKey.ArrowRight,
		isEditable: true,
		description: 'Следующий режим хендлинга транспорта',
	},

	/** HUD */
	HudChatShow: {
		defaultKey: InputKey.T,
		isEditable: true,
		description: 'Показать чат',
	},
	HudKeysHelpShow: {
		defaultKey: InputKey.F7,
		isEditable: false,
	},

	/** Phone */
	PhoneToggle: {
		defaultKey: InputKey.ArrowUp,
		isEditable: false,
		description: 'Достать/Убрать телефон',
	},

	/** PhotoMode */
	PhotoModeMoveForwards: {
		defaultKey: InputKey.W,
		isEditable: false,
	},
	PhotoModeMoveLeft: {
		defaultKey: InputKey.A,
		isEditable: false,
	},
	PhotoModeMoveBackwards: {
		defaultKey: InputKey.S,
		isEditable: false,
	},
	PhotoModeMoveRight: {
		defaultKey: InputKey.D,
		isEditable: false,
	},
	PhotoModeRollLeft: {
		defaultKey: InputKey.Q,
		isEditable: false,
	},
	PhotoModeRollRight: {
		defaultKey: InputKey.E,
		isEditable: false,
	},
	PhotoModeMoveUp: {
		defaultKey: InputKey.Space,
		isEditable: false,
	},
	PhotoModeMoveDown: {
		defaultKey: InputKey.Ctrl,
		isEditable: false,
	},
	PhotoModeMoveSlow: {
		defaultKey: InputKey.Alt,
		isEditable: false,
	},
	PhotoModeMoveFast: {
		defaultKey: InputKey.LeftShift,
		isEditable: false,
	},
	PhotoModeToggleDev: {
		defaultKey: InputKey.O,
		isEditable: false,
	},
	PhotoModeToggleInterfaceHidden: {
		defaultKey: InputKey.I,
		isEditable: false,
	},
	PhotoModeToggleMode: {
		defaultKey: InputKey.U,
		isEditable: false,
	},
	PhotoModeToggleFilter: {
		defaultKey: InputKey.F,
		isEditable: false,
	},
	PhotoModeToggleLogoPosition: {
		defaultKey: InputKey.C,
		isEditable: false,
	},
	PhotoModeToggleFormat: {
		defaultKey: InputKey.N,
		isEditable: false,
	},
	PhotoModeToggleGrid: {
		defaultKey: InputKey.H,
		isEditable: false,
	},
	PhotoModeResetSettings: {
		defaultKey: InputKey.R,
		isEditable: false,
	},

	/** Airbreak */
	AirbreakToggle: {
		defaultKey: InputKey.RightShift,
		isEditable: false,
	},

	/** Camhack */
	CamhackToggleOrTeleport: {
		defaultKey: InputKey.F4,
		isEditable: false,
	},
	CamhackToggleFromSpectate: {
		defaultKey: InputKey.F,
		isEditable: false,
	},

	/** HomeBuying */
	HomeBuyingCloseHome: {
		defaultKey: InputKey.C,
		isEditable: false,
	},
	HomeBuyingSellHome: {
		defaultKey: InputKey.R,
		isEditable: false,
	},
	HomeBuyingBack: {
		defaultKey: InputKey.Backspace,
		isEditable: false,
	},

	/** Handling */
	HandlingSave: {
		defaultKey: InputKey.S,
		isEditable: false,
	},
	HandlingSetFavorite: {
		defaultKey: InputKey.V,
		isEditable: false,
	},
	HandlingPanelInteract: {
		defaultKey: InputKey.B,
		isEditable: false,
	},
	HandlingResetAll: {
		defaultKey: InputKey.R,
		isEditable: false,
	},
	/** ClothesShop */
	ClothesShopToggleCart: {
		defaultKey: InputKey.R,
		isEditable: false,
	},

	/** CarPaint */
	CarPaintShowCart: {
		defaultKey: InputKey.R,
		isEditable: false,
	},

	/** Tuning */
	TuningShowCart: {
		defaultKey: InputKey.R,
		isEditable: false,
	},

	/** GameplayRecording */
	GameplayRecordingStart: {
		defaultKey: InputKey.F5,
		isEditable: false,
	},
	GameplayRecordingStop: {
		defaultKey: InputKey.F6,
		isEditable: false,
	},

	/** Gameplay */
	GameplayTogglePauseMenu: {
		defaultKey: InputKey.Escape,
		isEditable: false,
		description: 'Показать/Скрыть меню паузы (GTA)',
	},

	/** CharacterCreation */
	CharacterCreationChangeGender: {
		defaultKey: InputKey.C,
		isEditable: false,
	},

	CharacterCreationRandom: {
		defaultKey: InputKey.R,
		isEditable: false,
	},

	/** MainMenu */
	MainMenuToggle: {
		defaultKey: InputKey.Tab,
		isEditable: false,
	},

	/** RadialMenu */
	RadialMenuToggle: {
		defaultKey: InputKey.R,
		isEditable: false,
	},

	/** Voice */
	VoiceActivate: {
		defaultKey: InputKey.Z,
		isEditable: false,
	},

	/** LootBuyers */
	LootBuyersSellAll: {
		defaultKey: InputKey.R,
		isEditable: false,
	},

	/** HobbyFishing */
	HobbyFishingCatchFishSpace: {
		defaultKey: InputKey.Space,
		isEditable: false,
	},
	HobbyFishingCatchFishA: {
		defaultKey: InputKey.A,
		isEditable: false,
	},
	HobbyFishingCatchFishD: {
		defaultKey: InputKey.D,
		isEditable: false,
	},
	HobbyFishingStart: {
		defaultKey: InputKey.Space,
		isEditable: false,
	},

	/** GasStation */
	GasStationDrainFuel: {
		defaultKey: InputKey.C,
		isEditable: false,
	},
	GasStationFullTank: {
		defaultKey: InputKey.F,
		isEditable: false,
	},
	GasStationNavLeft: {
		defaultKey: InputKey.A,
		isEditable: false,
	},
	GasStationNavRight: {
		defaultKey: InputKey.D,
		isEditable: false,
	},

	/** Inventory */
	InventoryFastMove: {
		defaultKey: InputKey.LeftShift,
		isEditable: false,
	},
	InventoryPageToggle: {
		defaultKey: InputKey.I,
		isEditable: false,
	},

	InventoryUseHotbarSlot1: {
		defaultKey: InputKey.One,
		isEditable: false,
	},
	InventoryUseHotbarSlot2: {
		defaultKey: InputKey.Two,
		isEditable: false,
	},
	InventoryUseHotbarSlot3: {
		defaultKey: InputKey.Three,
		isEditable: false,
	},
	InventoryUseHotbarSlot4: {
		defaultKey: InputKey.Four,
		isEditable: false,
	},

	/** CatchRing */
	CatchRingCatch: {
		defaultKey: InputKey.Space,
		isEditable: false,
	},

	/** RockPaperScissors */
	RockPaperScissorsSelectRock: {
		defaultKey: InputKey.A,
		isEditable: false,
	},
	RockPaperScissorsSelectPaper: {
		defaultKey: InputKey.D,
		isEditable: false,
	},
	RockPaperScissorsSelectScissors: {
		defaultKey: InputKey.S,
		isEditable: false,
	},

	/** Invite */
	InviteAccept: {
		defaultKey: InputKey.Y,
		isEditable: false,
	},
	InviteDecline: {
		defaultKey: InputKey.N,
		isEditable: false,
	},

	/** FeedbackLight */
	FeedbackOpen: {
		defaultKey: InputKey.P,
		isEditable: false,
	},

	/** AdminPanel */
	AdminPanelToggle: {
		defaultKey: InputKey.K,
		isEditable: false,
	},
};

export enum InputComboAction {
	/** HUD */
	HudToggleVisibility = 'hud:toggle_visibility',
}

const inputComboActionsConfig: { [key in keyof typeof InputComboAction]: IMultiActionConfig } = {
	/** HUD */
	HudToggleVisibility: {
		defaultKeys: [InputKey.LeftShift, InputKey.O],
		isEditable: false,
	},
};

export function getInputActionConfig(action: InputAction): IActionConfig {
	const configName = action
		.split(/[:_]/)
		.map((entry) => {
			return entry.charAt(0).toUpperCase() + entry.slice(1);
		})
		.join('') as keyof typeof inputActionsConfig;

	const actionConfig = inputActionsConfig[configName] ?? null;
	if (actionConfig === null) {
		throw new Error(`Cannot find actionConfig for action "${action}"!`);
	}

	return actionConfig;
}

export function getInputComboActionConfig(multiAction: InputComboAction): IMultiActionConfig {
	const configName = multiAction
		.split(/[:_]/)
		.map((entry) => {
			return entry.charAt(0).toUpperCase() + entry.slice(1);
		})
		.join('') as keyof typeof inputComboActionsConfig;

	const multiActionConfig = inputComboActionsConfig[configName] ?? null;
	if (multiActionConfig === null) {
		throw new Error(`Cannot find multiActionConfig for multiAction "${multiAction}"!`);
	}

	return multiActionConfig;
}
